import { useCallback } from 'react';
import { useService } from '@atlassian/jira-common-services/src/use-service';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useServiceDeskContext } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/servicedesk-context/index.tsx';
import type { ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../../common/constants';
import type { RequestTypesResponse, ReturnObject } from './types';

export const getRequestTypes = async (
	serviceDeskId: number,
	searchQuery: string,
): Promise<RequestTypesResponse | undefined> =>
	performGetRequest(
		`/rest/servicedeskapi/servicedesk/${serviceDeskId}/requesttype?limit=100&restrictionStatus=open&searchQuery=${searchQuery}`,
	).catch((error: Error) => {
		fireErrorAnalytics({
			meta: {
				id: 'getRequestTypes',
				packageName: PACKAGE_NAME,
				teamName: TEAM_NAME,
			},
			error,
			sendToPrivacyUnsafeSplunk: true,
		});
		throw error;
	});

export const useGetRequestTypes = (projectKey: ProjectKey): ReturnObject => {
	const [{ data: serviceDeskContext }] = useServiceDeskContext(projectKey);
	const { serviceDeskId } = serviceDeskContext || {};
	const request = useCallback(
		(searchQuery: string) =>
			serviceDeskId ? getRequestTypes(serviceDeskId, searchQuery) : Promise.resolve(undefined),
		[serviceDeskId],
	);
	const { loading, error, data, fetch } = useService<RequestTypesResponse, string>(request);
	return [{ loading, error, data: data ? data.values : undefined }, fetch];
};
